import {
  selectOrganizationId,
  useGetTourQuery,
  useGetWorkspaceQuery,
  useSaveTourMutation,
  useUpdateTourDetailsMutation,
} from '@carboncopyco/data';
import { ColorHex, LoadingDots, Tooltip, notify } from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { UseNavigate } from '../../types/dependencies';
import { PublishTourEdit } from './PublishTourEdit';

interface PublishTourModalProps {
  buttonRef: React.RefObject<any>;
  onClose: () => void;
  visible: boolean;
  tourId?: number;
  tourName?: string;
  onPublishTour?: () => void;
  isTourModified?: boolean;
  isPublishInProgress: boolean;
  useNavigate: UseNavigate;
  onPublishAndExit?: () => void;
}

export const PublishTourModal = ({
  buttonRef,
  onClose,
  visible,
  tourId,
  tourName,
  onPublishTour,
  isTourModified,
  isPublishInProgress,
  useNavigate,
  onPublishAndExit,
}: PublishTourModalProps) => {
  const { navigateToTourPageByWorkspace } = useNavigate();
  const flags = useFlags();
  const organizationId = useSelector(selectOrganizationId);
  const { workspaceId } = useParams();
  const { data: workspace } = useGetWorkspaceQuery(
    { organizationId, id: workspaceId },
    { skip: !flags.platformWorkspaces }
  );
  const { data: tour } = useGetTourQuery({
    organizationId,
    workspaceId,
    id: tourId,
  });
  const [saveTour, { isLoading: saveTourIsLoading }] = useSaveTourMutation();
  const [updateTour] = useUpdateTourDetailsMutation();
  const [isPublishInitiated, setIsPublishInitiated] = useBoolean(false);
  const [
    isNavigateToTourPageAfterPublishInitiated,
    setIsNavigateToTourPageAfterPublishInitiated,
  ] = useBoolean();

  const handlePublishTour = (form: Record<string, string>) => {
    const newDescription = form.description;
    saveTour({
      organizationId,
      id: tourId,
      comment: newDescription,
    });
    updateTour({
      organizationId,
      id: tourId,
      tourName: form.tourName,
      description: newDescription,
    })
      .unwrap()
      .then(() => {
        notify.success('Your tour was saved successfully!');
        onClose();
      });
    setIsPublishInitiated.setTrue();
  };

  useEffect(() => {
    if (!saveTourIsLoading && isPublishInitiated) {
      onPublishTour();
      setIsPublishInitiated.setFalse();
    }
  }, [saveTourIsLoading, isPublishInitiated]);

  const isInProgress =
    isPublishInitiated || isPublishInProgress || saveTourIsLoading;

  useEffect(() => {
    if (isNavigateToTourPageAfterPublishInitiated && !isTourModified) {
      navigateToTourPageByWorkspace(tourId, workspaceId);
      onPublishAndExit?.();
      setIsNavigateToTourPageAfterPublishInitiated.setFalse();
    }
  }, [isNavigateToTourPageAfterPublishInitiated, isTourModified]);

  return (
    <Tooltip
      visible={visible}
      anchorRef={buttonRef}
      noArrow
      placement="bottom-end"
      interactive
      shadow="md"
    >
      <div className="p-5">
        {tour && !isInProgress ? (
          <PublishTourEdit
            onCancel={onClose}
            publishTour={handlePublishTour}
            tourName={tourName}
            tourDescription={tour.description}
            isFirstRevision={tour.revisionId === 0}
            workspace={workspace}
            initiateNavigateToTourPageAfterPublish={() =>
              setIsNavigateToTourPageAfterPublishInitiated.setTrue()
            }
          />
        ) : (
          <div className="justify-center flex w-ds-sm-modal h-ds-sm-modal">
            <LoadingDots color={ColorHex.natural600} />
          </div>
        )}
      </div>
    </Tooltip>
  );
};
